import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sun, Zap, Shield, Leaf, TrendingDown, Smartphone, Award, ChevronDown } from "lucide-react";
import usePageMeta from "./hooks/usePageMeta";

function Igreen() {
  usePageMeta("Pavan e Parazzi iGreen", "pp");
  
  const [scrolled, setScrolled] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Fixo */}
      <header 
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/e2onqydh_Logo%20igreen%202%20-%20sem%20fundo.png" 
                alt="Igreen Energy" 
                className="h-16 w-auto"
              />
            </Link>

            {/* Menu Desktop */}
            <nav className="hidden md:flex space-x-8">
              <button 
                onClick={() => scrollToSection('beneficios')}
                className="text-gray-700 hover:text-[#1A472A] font-medium transition-colors"
              >
                Benefícios
              </button>
              <button 
                onClick={() => scrollToSection('como-funciona')}
                className="text-gray-700 hover:text-[#1A472A] font-medium transition-colors"
              >
                Como Funciona
              </button>
              <button 
                onClick={() => scrollToSection('lei-14300')}
                className="text-gray-700 hover:text-[#1A472A] font-medium transition-colors"
              >
                Lei 14.300
              </button>
              <button 
                onClick={() => scrollToSection('app')}
                className="text-gray-700 hover:text-[#1A472A] font-medium transition-colors"
              >
                App Igreen Club
              </button>
              <button 
                onClick={() => scrollToSection('parceria')}
                className="text-gray-700 hover:text-[#1A472A] font-medium transition-colors"
              >
                Parceria PP
              </button>
            </nav>

            {/* Botão de Ação */}
            <Link 
              to="/igreencadastro"
              className="hidden md:block px-6 py-3 bg-[#1A472A] hover:bg-[#0f2818] text-white rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Quero Economizar Agora
            </Link>

            {/* Menu Mobile */}
            <button className="md:hidden text-[#1A472A]">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Spacer */}
      <div className="h-20"></div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background com efeito parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1509391366360-2e959784a276?q=80&w=2072')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#1A472A]/90 via-[#1A472A]/80 to-green-900/90"></div>
        </div>

        {/* Conteúdo */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 animate-fade-in">
            Pague menos na sua conta de luz com energia solar compartilhada!
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-12 animate-fade-in-delay">
            Economize até 15% na sua fatura de energia com a Igreen Energy — uma empresa do grupo Vibra, em parceria com Pavan & Parazzi.
          </p>
          <Link 
            to="/igreencadastro"
            className="inline-block px-12 py-5 bg-[#FFD700] hover:bg-yellow-500 text-[#1A472A] rounded-full font-bold text-xl transition-all duration-300 hover:scale-110 shadow-2xl animate-pulse-slow"
          >
            Sim, quero economizar!
          </Link>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <ChevronDown className="text-white w-8 h-8" />
          </div>
        </div>
      </section>

      {/* Seção: Por que escolher a Igreen? */}
      <section id="beneficios" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A472A] mb-4">
              Por que escolher a Igreen?
            </h2>
            <p className="text-xl text-gray-600">
              Benefícios que fazem a diferença no seu bolso e no planeta
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <TrendingDown className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Economia garantida
              </h3>
              <p className="text-gray-600 text-lg">
                Até <strong>15% de desconto</strong> na conta de energia todos os meses.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Sem investimento inicial
              </h3>
              <p className="text-gray-600 text-lg">
                <strong>Nenhum custo</strong> de instalação. Comece a economizar imediatamente.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Cadastro rápido e seguro
              </h3>
              <p className="text-gray-600 text-lg">
                Em poucos minutos, <strong>100% online</strong> e sem burocracia.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Leaf className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Energia limpa
              </h3>
              <p className="text-gray-600 text-lg">
                Proveniente de <strong>usinas solares</strong> em todo o Brasil.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Award className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Empresa confiável
              </h3>
              <p className="text-gray-600 text-lg">
                Parte do <strong>grupo Vibra</strong>, referência nacional em energia.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
              <div className="bg-[#1A472A] w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sun className="text-[#FFD700] w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold text-[#1A472A] mb-4">
                Sustentabilidade
              </h3>
              <p className="text-gray-600 text-lg">
                Ajude o planeta enquanto <strong>economiza dinheiro</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seção: Como Funciona */}
      <section id="como-funciona" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A472A] mb-4">
              Como funciona a energia solar compartilhada
            </h2>
            <p className="text-xl text-gray-600">
              Simples, transparente e eficiente
            </p>
          </div>

          {/* Fluxo visual */}
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            {/* Step 1 */}
            <div className="text-center relative">
              <div className="bg-[#1A472A] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Sun className="text-[#FFD700] w-10 h-10" />
              </div>
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[#FFD700]"></div>
              <h3 className="text-xl font-bold text-[#1A472A] mb-2">1. Geração Solar</h3>
              <p className="text-gray-600">
                As usinas solares da Igreen geram energia limpa
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative">
              <div className="bg-[#1A472A] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Zap className="text-[#FFD700] w-10 h-10" />
              </div>
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[#FFD700]"></div>
              <h3 className="text-xl font-bold text-[#1A472A] mb-2">2. Injeção na Rede</h3>
              <p className="text-gray-600">
                Essa energia é injetada na rede elétrica
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="bg-[#1A472A] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Shield className="text-[#FFD700] w-10 h-10" />
              </div>
              <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-[#FFD700]"></div>
              <h3 className="text-xl font-bold text-[#1A472A] mb-2">3. Fornecimento Normal</h3>
              <p className="text-gray-600">
                Você continua recebendo energia da distribuidora local
              </p>
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="bg-[#1A472A] w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <TrendingDown className="text-[#FFD700] w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-[#1A472A] mb-2">4. Desconto Automático</h3>
              <p className="text-gray-600">
                O crédito solar gera desconto direto na sua fatura
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seção: Lei 14.300 */}
      <section id="lei-14300" className="py-20 bg-gradient-to-br from-[#1A472A] to-green-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/20">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Lei nº 14.300/2022
              </h2>
              <div className="w-24 h-1 bg-[#FFD700] mx-auto mb-8"></div>
            </div>
            
            <blockquote className="text-lg md:text-xl text-white/90 italic leading-relaxed mb-8">
              "A Lei 14.300/2022, conhecida como o <strong>Marco Legal da Geração Distribuída</strong>, permite que qualquer consumidor escolha um fornecedor de energia renovável, como a Igreen, recebendo <strong>descontos mensais diretamente na conta de luz</strong>."
            </blockquote>

            <div className="text-center">
              <button 
                onClick={() => setShowModal(true)}
                className="px-8 py-4 bg-[#FFD700] hover:bg-yellow-500 text-[#1A472A] rounded-full font-bold transition-all duration-300 hover:scale-105 shadow-xl"
              >
                Saiba mais sobre a Lei 14.300
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Seção: App Igreen Club */}
      <section id="app" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Mockups */}
            <div className="relative">
              <div className="flex justify-center space-x-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/fky259j9_Imagem%20do%20WhatsApp%20de%202025-11-12%20%C3%A0%28s%29%2010.01.27_58c03a1d.jpg" 
                  alt="App Igreen 1"
                  className="w-48 md:w-64 shadow-2xl rounded-3xl transform hover:scale-105 transition-transform duration-300"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/as263zf4_Imagem%20do%20WhatsApp%20de%202025-11-12%20%C3%A0%28s%29%2010.01.27_ad65cb65.jpg" 
                  alt="App Igreen 2"
                  className="w-48 md:w-64 shadow-2xl rounded-3xl transform hover:scale-105 transition-transform duration-300 mt-8"
                />
              </div>
            </div>

            {/* Conteúdo */}
            <div>
              <div className="flex items-center mb-6">
                <Smartphone className="text-[#1A472A] w-12 h-12 mr-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-[#1A472A]">
                  Aplicativo Igreen Club
                </h2>
              </div>
              
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Acompanhe suas faturas, veja sua economia e aproveite <strong>descontos em centenas de empresas parceiras</strong> através do app Igreen Club, disponível na App Store e Google Play.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <div className="bg-[#1A472A] rounded-full p-2 mr-4 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#FFD700]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700 text-lg">Acompanhe sua economia em tempo real</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#1A472A] rounded-full p-2 mr-4 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#FFD700]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700 text-lg">Descontos exclusivos em grandes lojas</p>
                </div>
                <div className="flex items-start">
                  <div className="bg-[#1A472A] rounded-full p-2 mr-4 flex-shrink-0">
                    <svg className="w-4 h-4 text-[#FFD700]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-gray-700 text-lg">Gestão completa das suas faturas</p>
                </div>
              </div>

              <Link 
                to="/igreencadastro"
                className="inline-block px-8 py-4 bg-[#1A472A] hover:bg-[#0f2818] text-white rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
              >
                Quero Esses Benefícios Agora
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Seção: Parceria PP */}
      <section id="parceria" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A472A] mb-8">
              Parceria com Pavan & Parazzi
            </h2>
            <div className="flex justify-center items-center space-x-8 md:space-x-16 mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/7w39hvkz_Logo%20PP%20sem%20fundo.png" 
                alt="Pavan & Parazzi"
                className="h-24 md:h-32 w-auto animate-fade-in"
              />
              <div className="text-4xl text-[#FFD700] font-bold">+</div>
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/e2onqydh_Logo%20igreen%202%20-%20sem%20fundo.png" 
                alt="Igreen Energy"
                className="h-24 md:h-32 w-auto animate-fade-in-delay"
              />
            </div>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              A <strong>Pavan & Parazzi</strong> é parceira oficial da <strong>Igreen Energy</strong>, trazendo para você uma solução confiável, sem burocracia e com todo o suporte necessário para economizar e ajudar o planeta.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A472A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo e Info */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_finance-dash-68/artifacts/e2onqydh_Logo%20igreen%202%20-%20sem%20fundo.png" 
                alt="Igreen Energy"
                className="h-16 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-white/80 text-sm">
                Igreen Energy – Energia solar compartilhada de forma simples, sustentável e segura.
              </p>
              <p className="text-white/80 text-sm mt-2">
                <strong>Parceria oficial: Pavan & Parazzi</strong>
              </p>
            </div>

            {/* Links Rápidos */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Links Úteis</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Política de Privacidade
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Termos de Uso
                  </a>
                </li>
                <li>
                  <a href="#" className="text-white/80 hover:text-white transition-colors">
                    Contato
                  </a>
                </li>
              </ul>
            </div>

            {/* Grupo Vibra */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Grupo Vibra</h3>
              <p className="text-white/80 text-sm">
                A Igreen Energy é do <strong>Grupo Vibra</strong>, uma das maiores empresas de energia do Brasil, garantindo confiabilidade e excelência em todos os serviços.
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 pt-8 text-center">
            <p className="text-white/70 text-sm">
              © 2025 Pavan e Parazzi – Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Modal Lei 14.300 */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-bold text-[#1A472A]">
                Lei nº 14.300/2022
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="prose prose-lg">
              <p className="text-gray-700 mb-4">
                A <strong>Lei 14.300/2022</strong>, também conhecida como <strong>Marco Legal da Geração Distribuída</strong>, regulamenta a micro e minigeração distribuída no Brasil.
              </p>
              <h4 className="text-[#1A472A] font-bold mb-2">Principais pontos:</h4>
              <ul className="text-gray-700 space-y-2">
                <li>Permite que consumidores escolham fornecedores de energia renovável</li>
                <li>Garante descontos na conta de luz através de créditos de energia</li>
                <li>Incentiva o uso de fontes limpas e sustentáveis</li>
                <li>Estabelece regras claras para geração compartilhada</li>
                <li>Protege os direitos do consumidor</li>
              </ul>
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  Para mais informações oficiais, visite o site do 
                  <a href="https://www.planalto.gov.br" target="_blank" rel="noopener noreferrer" className="text-[#1A472A] font-bold ml-1">
                    Planalto
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Igreen;
